/**
 * Den-Route Affinity Map (v0.1)
 *
 * Drives the DenSwitcherPill's sidebar filtering. Each key is a route
 * path or a parent-prefix; each value is an array of persona_keys
 * (from `den_catalog.persona_key`) that should see the route, OR
 * `['*']` for "always show, regardless of active Den".
 *
 * Matching rules in shouldShowRouteForDen():
 *   1. If `showAllSurfaces` is true → always show.
 *   2. Exact path match wins (e.g. '/dashboard/scans').
 *   3. Otherwise, longest matching prefix wins (a route '/dashboard/foxhole/pipeline'
 *      under prefix '/dashboard/foxhole' is governed by the prefix entry).
 *   4. If no entry matches, default to TRUE (over-show is better than under-show
 *      — items missing from the map can be added later without breaking nav).
 *
 * Persona keys used here MUST match `den_catalog.persona_key` exactly:
 *   founder, athlete, recruiter, physician, lawyer, investor, author,
 *   politician, activist, religious, real_estate_agent, sales_pro, coach,
 *   speaker, fractional_executive, financial_advisor, consultant,
 *   podcast_host, real_estate_investor, course_creator, creative_freelancer,
 *   therapist, executive, journalist, academic, artist, military
 */

export type DenPersonaKey =
  | 'founder' | 'athlete' | 'recruiter' | 'physician' | 'lawyer' | 'investor'
  | 'author' | 'politician' | 'activist' | 'religious' | 'real_estate_agent'
  | 'sales_pro' | 'coach' | 'speaker' | 'fractional_executive'
  | 'financial_advisor' | 'consultant' | 'podcast_host' | 'real_estate_investor'
  | 'course_creator' | 'creative_freelancer' | 'therapist' | 'executive'
  | 'journalist' | 'academic' | 'artist' | 'military';

export type DenAffinity = DenPersonaKey[] | ['*'];

export const DEN_ROUTE_AFFINITIES: Record<string, DenAffinity> = {
  // ── Universal anchors (every Den sees these) ───────────────────────
  '/dashboard': ['*'],
  '/dashboard/my-den': ['*'],
  '/dashboard/dens': ['*'],
  '/welcome': ['*'],
  '/dashboard/welcome': ['*'],
  '/dashboard/scans': ['*'],
  '/dashboard/mentions': ['*'],
  '/dashboard/insights': ['*'],
  '/dashboard/geo-seo': ['*'],
  '/dashboard/geofixer': ['*'],
  '/dashboard/seofixer': ['*'],
  '/dashboard/find': ['*'],
  '/dashboard/people-finder': ['*'],
  '/dashboard/company-finder': ['*'],
  '/dashboard/contacts': ['*'],
  '/dashboard/mentioners': ['*'],

  // ── Vetting + comparison surfaces (universal — every Den vets people) ─
  '/dashboard/vetting-suite': ['*'],
  '/dashboard/compare': ['*'],
  '/dashboard/compare-people': ['*'],
  '/dashboard/compare-companies': ['*'],
  '/dashboard/evaluate': ['*'],
  '/dashboard/saved': ['*'],
  '/dashboard/saved-dossiers': ['*'],
  '/dashboard/dossiers': ['*'],

  // ── Engage surfaces (universal — every Den eventually reaches out) ──
  '/dashboard/outreach-review': ['*'],
  '/dashboard/twitter-outreach-review': ['*'],
  '/dashboard/closer-review': ['*'],
  '/dashboard/citation-leads': ['*'],
  '/dashboard/dealflow': ['*'],
  '/dashboard/sequences': ['*'],
  '/dashboard/send': ['*'],
  '/dashboard/content': ['*'],
  '/dashboard/content-creator': ['*'],
  '/dashboard/upcoming-meetings': ['*'],
  '/dashboard/engage': ['*'],

  // ── Setup / control (universal) ────────────────────────────────────
  '/dashboard/setup': ['*'],
  '/settings/profile': ['*'],
  '/settings/company-profile': ['*'],
  '/settings/foxchat': ['*'],
  '/settings/mcp': ['*'],
  '/settings/brands': ['*'],
  '/dashboard/settings': ['*'],
  '/dashboard/company-profile': ['*'],
  '/dashboard/integrations': ['*'],
  '/dashboard/data-quality': ['*'],
  '/dashboard/sites': ['*'],
  '/dashboard/my-team': ['*'],
  '/dashboard/billing': ['*'],
  '/dashboard/pricing': ['*'],
  '/dashboard/team': ['*'],
  '/dashboard/portals': ['*'],
  '/dashboard/audit-logs': ['*'],
  '/dashboard/custom-roles': ['*'],
  '/dashboard/brand-kit': ['*'],
  '/dashboard/brand-kits': ['*'],
  '/dashboard/monthly-reports': ['*'],
  '/dashboard/reports': ['*'],
  '/dashboard/clients': ['*'],
  '/clients': ['*'],
  '/clients/new': ['*'],
  '/dashboard/brands': ['*'],
  '/brands': ['*'],

  // ── Markets — universal (any Den might track companies / coins) ────
    '/dashboard/markets': ['*'],

  // ── Foxhole + Investor-Vetter surfaces — Investor / RE-Investor + Founder ─
  // FOXHOLE-NAV-RESTORE (2026-05-17): added 'founder' to all Foxhole-family
  // routes. Founders use Foxhole tooling (Pipeline CRM for fundraising,
  // Anti-Portfolio for competitor tracking, Alumni Spinout Tracker for
  // team mapping). The original investor-only filter hid the entire
  // section from any founder-Den user.
  '/dashboard/foxhole': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/foxhole/pipeline': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/foxhole/anti-portfolio': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/foxhole/signals': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/foxhole/pipeline-watch': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/pipeline-crm': ['founder', 'investor', 'real_estate_investor', 'sales_pro'],
  '/dashboard/admin/foxhole-outbound': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/admin/foxhole-review': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/admin/foxhole-red-team': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/admin/foxhole-efficiency': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/admin/investor-dedup-review': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/admin/investor-db-velocity': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/investor-vetter': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/investor-vetter/alumni-watchlists': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/alumni-spinout-tracker': ['founder', 'investor', 'real_estate_investor'],
  '/dashboard/founder-vetter': ['founder', 'investor', 'real_estate_investor'],
  '/founder-vetter': ['founder', 'investor', 'real_estate_investor'],
  '/investor-vetter': ['founder', 'investor', 'real_estate_investor'],

  // ── HR / Talent surfaces — Recruiter, Founder (hiring), Exec, Frac-Exec ─
  '/dashboard/hr-tools': ['recruiter', 'founder', 'executive', 'fractional_executive'],
  '/dashboard/applicants': ['recruiter', 'founder', 'executive', 'fractional_executive'],
  '/dashboard/bulk-compare': ['recruiter', 'founder', 'executive', 'fractional_executive'],
  '/dashboard/comp': ['recruiter', 'founder', 'executive', 'fractional_executive'],
  '/dashboard/influencer-eval': ['recruiter', 'founder', 'speaker', 'podcast_host', 'creative_freelancer'],
  '/dashboard/influencer-calculator': ['recruiter', 'founder', 'speaker', 'podcast_host', 'creative_freelancer'],

  // ── Founder-flavoured tools — Founder primary, plus a few adjacent ─
  '/dashboard/launch-pack': ['founder', 'consultant', 'coach', 'creative_freelancer'],
  '/dashboard/launch-tools': ['founder', 'consultant', 'coach'],
  '/dashboard/foxdens': ['*'],
  // '/founder-vetter' — defined above under Foxhole family with broader affinity
  '/founder-fit': ['founder', 'investor'],
  '/founder-self-vetter': ['founder'],
  '/dashboard/founder-fit': ['founder', 'investor'],
  '/dashboard/yc-report': ['founder', 'investor'],
  '/yc-application-scorer': ['founder', 'investor'],
  '/accelerator-vetter': ['founder', 'investor'],
  '/entrepreneur-vetter': ['founder', 'investor'],

  // ── Persona-specific vetter pages ──────────────────────────────────
  '/executive-vetter': ['executive', 'fractional_executive', 'founder', 'recruiter'],
  '/wealth-advisor-vetter': ['financial_advisor', 'investor'],
  '/physician-vetter': ['physician', 'therapist'],
  '/veterinarian-vetter': ['physician'],
  // Journalism vetters are dual-listed in the Journalism sidebar group (the
  // discoverable home for journalism work) — always visible, like Get Press,
  // so journalists never need to discover the separate Vetting Suite. (HHH 2026-06-01)
  '/publication-vetter': ['*'],
  '/journalism-person-vetter': ['journalist', 'author', 'activist', 'politician'],
  '/journalist-vetter': ['*'],
  '/source-vetter': ['journalist', 'author'],
  '/pe-firm-vetter': ['investor', 'founder'],
  '/vc-firm-vetter': ['investor', 'founder'],
  '/specific-fund-vetter': ['investor', 'founder'],
  '/claims-check': ['*'],
  '/dashboard/manuscript': ['journalist', 'academic', 'author'],
  '/donor-vetter': ['activist', 'religious', 'politician'],
  '/expert-witness-vetter': ['lawyer'],
  '/counterparty-vetter': ['lawyer', 'investor'],
  '/litigation-funder-vetter': ['lawyer'],
  '/insurance-underwriting-vetter': ['financial_advisor'],
  '/crypto-coin-vetter': ['investor'],
  '/penny-stock-vetter': ['investor'],
  '/nft-collection-vetter': ['investor', 'artist'],
  '/spac-sponsor-vetter': ['investor'],
  '/pre-ipo-secondary-vetter': ['investor'],

  // ── Content tools — Author / Coach / Speaker / Podcast / Course ──
  '/dashboard/blog-generator': ['author', 'coach', 'speaker', 'consultant', 'course_creator', 'founder', 'journalist'],
  '/dashboard/faq-generator': ['*'],
  '/dashboard/content-library': ['*'],
  '/dashboard/about-page-generator': ['*'],
  '/dashboard/landing-pages': ['*'],
  '/dashboard/clips': ['*'],
  '/dashboard/proof': ['*'],
  '/dashboard/walkthroughs': ['*'],
  '/dashboard/sales-arsenal': ['*'],
  '/dashboard/capture-forms': ['*'],
  '/dashboard/slider-builder': ['*'],
  '/dashboard/comparison-card': ['*'],
  '/dashboard/trend-chart': ['*'],
  '/dashboard/press': ['*'],
  '/dashboard/embed': ['*'],
  '/dashboard/lead-pool': ['*'],

  // ── CRM (universal — sales tooling everyone gets) ──────────────────
  '/dashboard/crm': ['*'],

  // ── Live demo / sites / search (universal) ─────────────────────────
  '/dashboard/live-demo': ['*'],

  // ── Admin / superadmin (universal, gated elsewhere) ────────────────
  '/dashboard/admin': ['*'],
  '/dashboard/cc-queue': ['*'],
  '/dashboard/manager': ['*'],
  '/admin': ['*'],
  '/owner': ['*'],

  // ── Public catalog escape hatch ────────────────────────────────────
  '/dens': ['*'],
};

/**
 * Resolve whether a given route should be visible for an active Den.
 *
 * @param routePath   the route href (e.g. '/dashboard/foxhole/pipeline')
 * @param activeDen   the user's primary persona_key (null = no Den picked)
 * @param showAllSurfaces  the user's "Show all surfaces" escape-hatch toggle
 * @returns true if the sidebar item should render, false to hide it
 */
export function shouldShowRouteForDen(
  routePath: string,
  activeDen: string | null,
  showAllSurfaces: boolean,
): boolean {
  if (showAllSurfaces) return true;
  if (!routePath) return true;
  if (!activeDen) return true; // No Den picked → show everything (zero-Den state)
  if (routePath === '#' || routePath.startsWith('http')) return true;

  // 1. Exact match wins.
  const exact = DEN_ROUTE_AFFINITIES[routePath];
  if (exact) {
    if (exact[0] === '*') return true;
    return (exact as DenPersonaKey[]).includes(activeDen as DenPersonaKey);
  }

  // 2. Longest-prefix match.
  let bestPrefix = '';
  let bestAffinity: DenAffinity | null = null;
  for (const [prefix, affinity] of Object.entries(DEN_ROUTE_AFFINITIES)) {
    if (routePath === prefix || routePath.startsWith(prefix + '/')) {
      if (prefix.length > bestPrefix.length) {
        bestPrefix = prefix;
        bestAffinity = affinity;
      }
    }
  }
  if (bestAffinity) {
    if (bestAffinity[0] === '*') return true;
    return (bestAffinity as DenPersonaKey[]).includes(activeDen as DenPersonaKey);
  }

  // 3. Unclassified → over-show by default.
  return true;
}
